import cx from "classnames";
import { uniqueId } from "lodash";
import * as React from "react";
import { QESButton } from "./qes-standards/Button";
import { DragAndDropFileUploaderStateEnum, UploaderProps } from "./interfaces/dragAndDropUploader";
import { JSX } from "react";

const inputId = uniqueId("qes-file-upload-");

const DragAndDropFileUploader = (props: UploaderProps): JSX.Element => {
  const [boxState, setBoxState] = React.useState<DragAndDropFileUploaderStateEnum>(DragAndDropFileUploaderStateEnum.Neutral);
  const [files, setFiles] = React.useState<FileList>(null);
  const [error, setError] = React.useState<string[]>([]);
  const boxRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (!!props.onUploadStateChange) {
      props.onUploadStateChange(boxState);
    }
  }, [boxState]);

  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    // prevent default is necessary to allow the drop event later
    // https://developer.mozilla.org/en-US/docs/Web/API/HTML_Drag_and_Drop_API/Drag_operations#droptargets
    e.preventDefault();
    setBoxState(DragAndDropFileUploaderStateEnum.Hover);
  };

  const handleBrowseChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files.length) {
      setBoxState(DragAndDropFileUploaderStateEnum.Pending);
      setFiles(e.target.files);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setBoxState(DragAndDropFileUploaderStateEnum.Pending);
    setFiles(e.dataTransfer.files);
  };

  React.useEffect(() => {
    if (props.hasBeenSuccessful) {
      setBoxState(DragAndDropFileUploaderStateEnum.Success);
    }
  }, [props.hasBeenSuccessful]);

  const handleUpload = () => {
    if (!files) {
      setBoxState(DragAndDropFileUploaderStateEnum.Error);
      return;
    }
    const allowedTypes = props.filetype && props.filetype.split(",");
    if (allowedTypes && !allowedTypes.includes(files[0].type) && !allowedTypes.includes(`.${files[0].name.split(".").pop()}`)) {
      setBoxState(DragAndDropFileUploaderStateEnum.TypeError);
      return;
    }
    setBoxState(DragAndDropFileUploaderStateEnum.Dropped);

    const formData = new FormData();
    const file = [...files][0];
    formData.append(props.apiKeyName, file, file.name);
    try {
      props.onUpload(formData).then(
        () => {
          setBoxState(DragAndDropFileUploaderStateEnum.Success);
          setError([]);
        }, (errorResponse?: string[] | string) => {
          if (typeof errorResponse === "string") {
            setError([errorResponse]);
          } else {
            setError(errorResponse);
          }
          setBoxState(DragAndDropFileUploaderStateEnum.Error);
          inputRef.current.value = null;
        },
      );
    } catch (error) {
      setBoxState(DragAndDropFileUploaderStateEnum.Error);
      inputRef.current.value = null;
    }
  };

  const handleCancel = () => {
    setBoxState(DragAndDropFileUploaderStateEnum.Neutral);
    inputRef.current.value = null;
    setFiles(null);
  };

  const getBoxCopy = React.useMemo(() => {
    switch (boxState) {
      case DragAndDropFileUploaderStateEnum.Neutral:
        return <>Drag a file here or <label htmlFor={inputId} data-testid={props.testId} className="qes-file-uploader__label mb-0">Browse</label>.</>;
      case DragAndDropFileUploaderStateEnum.Hover:
        return <>Drop your file here.</>;
      case DragAndDropFileUploaderStateEnum.Pending:
        const uploadButton = props.allowUpload ?? true
          ? <QESButton qesType="primary" disabled={props.disabled} onClick={handleUpload} qesTestId="file-upload__continue">Continue</QESButton>
          : <QESButton qesType="disabled" qesTestId="file-upload__continue">Continue</QESButton>;
        return (
          <>
            File selected: {files[0].name}<br />
            {props.displayName && <>Display Title: {props.displayName}</>}
            <div className="offset-3 col-6 d-flex justify-content-between">
              <QESButton qesType="secondary" onClick={handleCancel} qesTestId="file-upload__cancel">Cancel</QESButton>
              {uploadButton}
            </div>
          </>
        );
      case DragAndDropFileUploaderStateEnum.Dropped:
        return <>We are uploading and processing your file.</>;
      case DragAndDropFileUploaderStateEnum.Error:
        if (error?.length) {
          return (
            <>
              There was an error processing your file:<br />
              <div className="mb-1 mt-1">{error.length === 1 ? error[0] : (<ul className="d-inline-block ps-0 mb-0 text-start">{error.map((e, i) => <li key={i}>{e}</li>)}</ul>)}</div>
              Please <label htmlFor={inputId} data-testid={props.testId} className="qes-file-uploader__label mb-0">try again</label>.
            </>
          );
        }
        return <>There was an error processing your file. Please <label htmlFor={inputId} data-testid={props.testId} className="qes-file-uploader__label mb-0">try again</label>.</>;
      case DragAndDropFileUploaderStateEnum.Success:
        return (
          <>
            <p>{(!!files && !!files[0] && files[0].name) ? files[0].name : "Your file"} was successfully uploaded.</p>
            <p><label htmlFor={inputId} data-testid={props.testId} className="qes-file-uploader__label mb-0">Browse</label> if you would like to upload a different file.</p>
          </>
        );
      case DragAndDropFileUploaderStateEnum.TypeError:
        return <>Please check your file is one of the following types and <label htmlFor={inputId} data-testid={props.testId} className="qes-file-uploader__label mb-0">try again</label>:<br /> {props.filetype}</>;
      default:
        return null;
    }
  }, [boxState, props.allowUpload, props.displayName, props.disabled, props.onUpload]);

  return (
    <div
      className={cx({
        "qes-file-uploader": true,
        "small": props.small,
      })}
    >
      {props.title && <h3 className="qes-file-uploader__title">{props.title}</h3>}
      <input type="file" id={inputId} className="qes-file-uploader__input" accept={props.filetype || null} onChange={(e) => handleBrowseChange(e)} ref={inputRef} />
      <div
        className={cx({
          "error": boxState === DragAndDropFileUploaderStateEnum.Error || boxState === DragAndDropFileUploaderStateEnum.TypeError,
          "hover": boxState === DragAndDropFileUploaderStateEnum.Hover,
          "info": boxState === DragAndDropFileUploaderStateEnum.Pending,
          "qes-file-uploader__drop-box": true,
          "success": boxState === DragAndDropFileUploaderStateEnum.Success,
        })}
        onDragLeave={() => setBoxState(DragAndDropFileUploaderStateEnum.Neutral)}
        onDragEnter={(e) => handleDrag(e)}
        onDragOver={(e) => handleDrag(e)}
        onDrop={(e) => handleDrop(e)}
        ref={boxRef}
      >
        <span role="alert" aria-live="assertive">
          {getBoxCopy}
        </span>
      </div>
    </div>
  );
};

export default DragAndDropFileUploader;
